import { inWindow, instant } from '../time.js'
import type { ConnectedEnvelopeRecord } from './connected.js'
import type { MetricWindow } from './types.js'

/**
 * Connected activity for the Insights charts — PRD §9.1 ("charts use explicit activation or event
 * cohorts"), §9.3.
 *
 * Both views below are cuts of the same activation cohort R10 counts: the envelopes activated in
 * W. So the chart's "sent" bars add up to R10's denominator, its "completed" bars add up to R09,
 * and the status breakdown's completed/declined/expired slices are R09/R07/R08 exactly. Nothing
 * here is a new metric; it only spreads registered counts over days or outcomes.
 */

export interface ConnectedActivityDay {
  /** Local calendar date in the declared timezone, `YYYY-MM-DD`. */
  readonly date: string
  /** Envelopes activated on this date. */
  readonly sent: number
  /** Of those, the ones with an accepted final commit by A. */
  readonly completed: number
}

/** Where the cohort stands as of A. The five parts sum to the cohort size. */
export interface ConnectedOutcomes {
  readonly completed: number
  readonly in_progress: number
  readonly declined: number
  readonly expired: number
  readonly voided: number
}

export interface ConnectedActivity {
  readonly timezone: string
  readonly days: readonly ConnectedActivityDay[]
  readonly outcomes: ConnectedOutcomes
}

const dateFormatters = new Map<string, Intl.DateTimeFormat>()

function dateFormatter(timezone: string): Intl.DateTimeFormat {
  const cached = dateFormatters.get(timezone)
  if (cached !== undefined) return cached
  // en-CA formats a date as YYYY-MM-DD.
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  dateFormatters.set(timezone, formatter)
  return formatter
}

/** Whether the runtime knows this IANA zone; an unknown one would throw on first use. */
export function isKnownTimezone(timezone: string): boolean {
  try {
    dateFormatter(timezone)
    return true
  } catch {
    return false
  }
}

/** The local calendar date of an instant, `YYYY-MM-DD`. */
export function zonedDateKey(atMs: number, timezone: string): string {
  const parts = dateFormatter(timezone).formatToParts(new Date(atMs))
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((entry) => entry.type === type)?.value ?? '00'
  return `${part('year')}-${part('month')}-${part('day')}`
}

/** Every calendar date from `first` to `last` inclusive (both `YYYY-MM-DD`). */
export function dateKeysBetween(first: string, last: string): readonly string[] {
  const [year, month, day] = first.split('-').map(Number) as [number, number, number]
  const keys: string[] = []
  for (let offset = 0; ; offset += 1) {
    // Date arithmetic on the date alone, in UTC, so no zone's DST can skip or repeat a day.
    const key = new Date(Date.UTC(year, month - 1, day + offset)).toISOString().slice(0, 10)
    if (key > last) break
    keys.push(key)
    if (offset > 400) break
  }
  return keys
}

function completedBy(envelope: ConnectedEnvelopeRecord, asOf: number): boolean {
  return envelope.final_commit_at !== undefined && instant(envelope.final_commit_at) <= asOf
}

function endedAs(
  envelope: ConnectedEnvelopeRecord,
  state: ConnectedEnvelopeRecord['state'],
  asOf: number,
): boolean {
  return (
    envelope.state === state &&
    envelope.terminal_at !== undefined &&
    instant(envelope.terminal_at) <= asOf
  )
}

export function computeConnectedActivity(
  envelopes: readonly ConnectedEnvelopeRecord[],
  options: { readonly as_of: string; readonly window: MetricWindow; readonly timezone: string },
): ConnectedActivity {
  const asOf = instant(options.as_of)
  const start = instant(options.window.start)
  const end = instant(options.window.end)
  const cohort = envelopes.filter((envelope) =>
    inWindow(instant(envelope.activated_at), start, end),
  )

  // The window's last instant is just before `end`; a window ending at midnight ends the day before.
  const keys = dateKeysBetween(
    zonedDateKey(start, options.timezone),
    zonedDateKey(Math.max(start, end - 1), options.timezone),
  )
  const sent = new Map<string, number>(keys.map((key) => [key, 0]))
  const completed = new Map<string, number>(keys.map((key) => [key, 0]))
  const outcomes = { completed: 0, in_progress: 0, declined: 0, expired: 0, voided: 0 }

  for (const envelope of cohort) {
    const key = zonedDateKey(instant(envelope.activated_at), options.timezone)
    sent.set(key, (sent.get(key) ?? 0) + 1)
    if (completedBy(envelope, asOf)) {
      completed.set(key, (completed.get(key) ?? 0) + 1)
      outcomes.completed += 1
    } else if (endedAs(envelope, 'DECLINED', asOf)) outcomes.declined += 1
    else if (endedAs(envelope, 'EXPIRED', asOf)) outcomes.expired += 1
    else if (endedAs(envelope, 'VOIDED', asOf)) outcomes.voided += 1
    else outcomes.in_progress += 1
  }

  return {
    timezone: options.timezone,
    days: keys.map((date) => ({
      date,
      sent: sent.get(date) ?? 0,
      completed: completed.get(date) ?? 0,
    })),
    outcomes,
  }
}
